/**
 * User-facing copy for the two password-reset screens.
 *
 * Both consume errors from `normalizePasswordResetError` (@utils/api/auth),
 * which guarantees `status`, `messages` (an array of strings) and
 * `isNetworkError`.
 */

const NETWORK_MESSAGE = "Couldn't reach the server. Check your connection and try again.";

const RATE_LIMIT_MESSAGE = 'Too many reset attempts. Please wait a few minutes and try again.';

/**
 * Step 1 - requesting a reset link.
 *
 * Deliberately ignores the server's prose. This endpoint answers 200 for an
 * unknown email so it cannot be used to enumerate accounts, which means the UI
 * must never claim the address was wrong -- the previous copy did exactly that
 * and sent a user looking for a typo that was not there.
 *
 * @param {{ status?: number, isNetworkError?: boolean }} error
 * @returns {string}
 */
export const getRequestResetErrorMessage = error => {
	if (error?.isNetworkError) return NETWORK_MESSAGE;
	if (error?.status === 401) return RATE_LIMIT_MESSAGE;

	return 'Something went wrong on our end. Please try again, or contact support if it keeps happening.';
};

/**
 * Step 2 - submitting a new password with the emailed token.
 *
 * Prefers the server's messages: "Expired code provided, please request a code
 * again." and "Invalid verification code provided" are already what the user
 * needs to read. Falls back only when nothing usable came back.
 *
 * @param {{ messages?: string[], isNetworkError?: boolean }} error
 * @returns {string[]} always at least one message
 */
export const getResetErrorMessages = error => {
	if (error?.isNetworkError) return [NETWORK_MESSAGE];
	if (error?.messages?.length) return error.messages;

	return ['Something went wrong resetting your password. Please request a new reset link.'];
};
